import React, { useCallback, useEffect } from 'react'
import { Linking } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useFocusEffect } from '@react-navigation/native'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { setConversationFields, addEventListener } from 'react-native-zendesk-messaging'

import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { logger } from 'APP/Lib/Datadog'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import Config from 'APP/Config'

import ListItem from 'APP/Converse/ListItem'
import List from 'APP/Components/List'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueBranding from 'APP/Components/DialogueBranding'

import { ResponsiveScrollView } from 'APP/Converse/Layout'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import { patientProfileState } from 'APP/Sagas/PatientSagas'
import { ApplicationStyles } from 'APP/Themes'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { zendeskActions } from 'APP/Store/Zendesk'
import { NotificationIconBadge } from 'APP/Components/NotificationIconBadge'
import { openZendesk, logUnreadCount } from 'APP/Services/Zendesk/Zendesk'
import { useDevTools } from 'APP/Lib/DevTools'

export type HelpCentreScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'helpCentre'
>

const HelpCentreScreen = (props: HelpCentreScreenProps) => {
  const { navigation, route } = props
  const {
    episodeId = '',
    isRegistrationPage = false,
    screenName = '',
  } = {
    ...route?.params,
    ...props,
  }
  const { enableZendeskChat } = useSelector(selectFeatures)
  const {
    eligibilityIntervals,
    id: memberId,
    email,
    preferredName,
    givenName,
    familyName,
  } = useSelector(patientProfileState)
  // String manipulated to handle Zendesk field constraints
  const productName = Config.PRODUCT_NAME.toLowerCase().replaceAll(' ', '_').replace('+', '')
  const activeInterval = eligibilityIntervals?.find(
    (interval: any) => interval?.status === 'active'
  )
  const dispatch = useDispatch()
  const org = activeInterval?.eligibilityRecord?.organization
  const ZENDESK_FIELD_IDS: Record<string, string | number> = Config.ZENDESK_FIELDS
  const buildWebConversationFields = (data: Record<string, string | number>) => {
    return Object.entries(data).map(([key, value]) => ({
      id: ZENDESK_FIELD_IDS[key as keyof typeof ZENDESK_FIELD_IDS],
      value,
    }))
  }
  const { clearTools } = useDevTools()
  const buildMobileConversationFields = (
    data: Record<string, any>,
    fieldIds: Record<string, string | number>
  ) => {
    const mobileFields: Record<string, string | number> = {}

    Object.entries(data).forEach(([key, value]) => {
      const id = fieldIds[key as keyof typeof fieldIds]
      if (id !== undefined) {
        mobileFields[id] = value ?? ''
      }
    })

    return mobileFields
  }

  // Special handling for the "app section" field as it matches the zendesk dropdown options
  const getAppSection = () => {
    if (isRegistrationPage) return 'registration'
    if (screenName.toLowerCase().includes('intake')) return 'intake'
    return 'account'
  }

  // In dev builds, the DevTools "?" button causes UI distortion in Mobile
  // This clears the DevTools button when navigating to HelpCentre.
  useFocusEffect(
    useCallback(() => {
      clearTools()
    }, [clearTools])
  )
  const goToSupportChat = async () => {
    Analytics.trackEvent('button_click', { button_value: 'HelpCentreScreen.chatWithUs' })

    const payload = {
      PRODUCT: productName,
      ORGANIZATION_ID: org?.id,
      ORGANIZATION_NAME: org?.name,
      EMAIL: email,
      FULL_NAME: `${preferredName || givenName} ${familyName}`,
      USER_ID: memberId,
      EPISODE_ID: episodeId,
      SCREEN_NAME: screenName,
      APP_SECTION: getAppSection(),
    }

    if (isWeb()) {
      const fields = buildWebConversationFields(payload)

      window?.zE?.('messenger:set', 'conversationFields', fields)
      window?.zE?.('messenger', 'show')
      window?.zE?.('messenger', 'open')
    } else {
      try {
        const mobileFields = buildMobileConversationFields(payload, ZENDESK_FIELD_IDS)
        setConversationFields(mobileFields)
        await openZendesk()
      } catch (error) {
        logger.error('Failed to open Zendesk chat', {}, error)
      }
    }
  }

  const goToEmail = () => {
    Analytics.trackEvent('button_click', { button_value: 'HelpCentreScreen.emailSupport' })
    const customContactUs = Config.CUSTOM_SUPPORT_PAGE
    if (customContactUs) {
      navigation.navigate('contactUs')
    } else {
      const url = UrlBuilder.supportEmailUrl()
      Linking.openURL(url).catch((e) => {
        logger.error('Failed to open support email link', { url }, e)
      })
    }
  }

  const goToFaq = () => {
    Analytics.trackEvent('button_click', { button_value: 'HelpCentreScreen.browseFaq' })
    const url = UrlBuilder.helpAndSupportUrl()
    Linking.openURL(url).catch((e) => {
      logger.error('Failed to open FAQ link', { url }, e)
    })
  }
  const unreadCount = useSelector((state) => state.zendesk.unreadCount)

  useEffect(() => {
    if (isWeb()) return
    let subscription

    const fetchUnread = async () => {
      const count = await logUnreadCount()
      dispatch(zendeskActions.setUnreadCount(count || 0))
    }

    fetchUnread()

    // subscribe to updates
    subscription = addEventListener('unreadMessageCountChanged', (event) => {
      if (event?.unreadCount !== undefined) {
        dispatch(zendeskActions.setUnreadCount(event.unreadCount))
      }
    })

    return () => {
      subscription?.remove?.()
    }
  }, [dispatch])

  const showChatWithUs = () => {
    if (isRegistrationPage) return enableZendeskChat
    return true
  }

  return (
    <NoticeBarContainer>
      <ResponsiveScrollView alwaysBounceVertical={false}>
        <List containerStyle={ApplicationStyles.screen.listContainer}>
          {showChatWithUs() && (
            <ListItem
              key="chatWithUs"
              title={I18n.t('HelpCentreScreen.chatWithUs')}
              subtitle={I18n.t('HelpCentreScreen.chatWithUsDescription')}
              onPress={goToSupportChat}
              IconComponent={() => <NotificationIconBadge unreadCount={unreadCount} />}
            />
          )}
          <ListItem
            key="emailSupport"
            title={
              Config.CUSTOM_SUPPORT_PAGE
                ? I18n.t('HelpCentreScreen.contactUs')
                : I18n.t('HelpCentreScreen.emailSupport')
            }
            subtitle={I18n.t('HelpCentreScreen.emailSupportDescription')}
            onPress={goToEmail}
            iconName={Config.CUSTOM_SUPPORT_PAGE ? 'chevron-right' : 'exit-to-app'}
          />
          <ListItem
            key="browseFaq"
            title={I18n.t('HelpCentreScreen.browseFaq')}
            subtitle={I18n.t('HelpCentreScreen.browseFaqDescription')}
            onPress={goToFaq}
            iconName="exit-to-app"
          />
        </List>
      </ResponsiveScrollView>
      <DialogueBranding />
    </NoticeBarContainer>
  )
}

export default HelpCentreScreen
